// Accessibility Statement for CareerOS-AI.
import LegalLayout, { bullets } from "./LegalLayout";

const EFFECTIVE = "July 16, 2026";

const SECTIONS = [
  { n: 1, id: "commitment", title: "Our commitment",
    body: <p>CareerOS-AI is committed to making the Service usable by as many people as possible, regardless of ability or the assistive technology they use. We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.2, Level AA — the standard referenced by the Accessibility for Ontarians with Disabilities Act (AODA) and equivalents in the EU (EN 301 549), the UK, and the US (Section 508).</p> },
  { n: 2, id: "what-we-do", title: "What we do today",
    body: (<>
      {bullets([
        "Semantic HTML and ARIA labeling on interactive elements.",
        "Keyboard navigation across all primary flows (login, dashboard, chat, resume, financial).",
        "Sufficient color contrast in both light and dark themes; theme is user-controlled.",
        "Focus indicators on all focusable elements.",
        "Descriptive alt text on images we ship (user-uploaded images are outside our control).",
        "Support for browser and OS text sizing without loss of content.",
        "Screen-reader friendly navigation on marketing and app pages.",
        "Motion-sensitive users: we respect the prefers-reduced-motion setting.",
      ])}
    </>) },
  { n: 3, id: "known-limitations", title: "Known limitations",
    body: (<>
      <p>We are actively improving. Current known limitations include:</p>
      {bullets([
        "Some complex data visualisations (financial-plan charts) may not yet be fully readable by screen readers; we provide the underlying numbers in accessible tables where possible.",
        "Live voice interview mode requires audio input; text alternatives are being expanded.",
        "AI-generated content may occasionally include formatting that assistive technology reads in unexpected ways. Report specific examples and we will address them.",
      ])}
    </>) },
  { n: 4, id: "feedback", title: "Feedback and requests",
    body: (<>
      <p>If you encounter a barrier or need help using CareerOS-AI with an assistive technology, please tell us:</p>
      <p className="not-prose mt-4 rounded-lg border border-border p-4 bg-card/60 font-mono text-sm leading-relaxed">
        Email: <a className="underline" href="mailto:support@careeros-ai.com">support@careeros-ai.com</a><br />
        Subject line: &quot;Accessibility&quot;<br />
        Please include: the page, the assistive technology, and what did not work.
      </p>
      <p>We aim to respond to accessibility feedback within 5 business days.</p>
    </>) },
  { n: 5, id: "alternative-formats", title: "Alternative formats",
    body: <p>Where a CareerOS-AI feature is not yet accessible to you, contact support and we will provide an alternative format or a manual workaround.</p> },
  { n: 6, id: "aoda", title: "AODA (Ontario)",
    body: <p>CareerOS-AI is headquartered in Ontario. In compliance with the Integrated Accessibility Standards Regulation (IASR) under AODA, we treat accessibility feedback as a priority support category and we maintain a rolling review of new features against WCAG 2.2 AA.</p> },
  { n: 7, id: "updates", title: "Updates to this statement",
    body: <p>We update this statement as we ship accessibility improvements. The &quot;Last Updated&quot; date reflects the current version.</p> },
];

const INTRO = (
  <p>Accessibility is a first-class design goal at CareerOS-AI. This statement describes our current commitment, what we have shipped, known gaps, and how to give us feedback.</p>
);

export default function Accessibility() {
  return (
    <LegalLayout
      eyebrow="Legal · Accessibility"
      title="Accessibility Statement"
      subtitle="for CareerOS-AI"
      effectiveDate={EFFECTIVE}
      lastUpdated={EFFECTIVE}
      intro={INTRO}
      sections={SECTIONS}
      currentPath="/accessibility"
    />
  );
}
